if (!customElements.get('kekka-nav-slider')) {
  customElements.define('kekka-nav-slider', class extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.header__inline-menu');
      if (!this.nav) return;

      this.items = Array.from(this.nav.querySelectorAll(
        ':scope > .list-menu > li > .header__menu-item, ' +
        ':scope > .list-menu > li > header-menu > details > .header__menu-item'
      ));
      if (!this.items.length) return;

      this.active = this.items.find(item => item.querySelector('.header__active-menu-item'));
      this.hovered = null;
      this.focused = null;
      this.motion = null;
      this.frame = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.reducedMotion.addEventListener('change', () => this.update(), options);
      this.indicator = document.createElement('span');
      this.indicator.className = 'kekka-nav-slider__indicator';
      this.indicator.setAttribute('aria-hidden', 'true');
      this.nav.prepend(this.indicator);

      this.items.forEach(item => {
        item.closest('li').addEventListener('pointerenter', event => {
          if (event.pointerType === 'touch') return;
          this.hovered = item;
          this.update();
        }, options);
      });
      this.nav.addEventListener('pointerleave', () => {
        this.hovered = null;
        this.update();
      }, options);
      this.nav.addEventListener('focusin', event => {
        this.focused = this.items.find(item =>
          item.closest('li').contains(event.target) && event.target.matches(':focus-visible')
        );
        this.update();
      }, options);
      this.nav.addEventListener('focusout', event => {
        if (!this.nav.contains(event.relatedTarget)) {
          this.focused = null;
          this.update();
        }
      }, options);

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.nav);
      this.items.forEach(item => this.resizeObserver.observe(item));
      this.update();
    }

    update() {
      const target = this.hovered || this.focused || this.active;
      const navRect = this.nav.getBoundingClientRect();
      if (window.innerWidth < 990 || !navRect.width || !navRect.height) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.motion = null;
        this.nav.classList.remove('kekka-nav-slider--ready', 'kekka-nav-slider--away');
        this.indicator.style.opacity = '0';
        return;
      }

      this.items.forEach(item => item.classList.toggle('kekka-nav-slider__target', item === target));
      if (!target) {
        this.indicator.style.opacity = '0';
        this.nav.classList.remove('kekka-nav-slider--away');
        return;
      }

      const label = target.querySelector('span') || target;
      const rect = label.getBoundingClientRect();
      const activeLabel = this.active?.querySelector('.header__active-menu-item');
      // Match the existing active label's padding and its 11px pseudo-element overhang.
      const activeStyle = activeLabel && getComputedStyle(activeLabel);
      const padding = activeStyle
        ? parseFloat(activeStyle.paddingLeft) + parseFloat(activeStyle.paddingRight)
        : 44;
      const desiredWidth = rect.width + 22 + (target === this.active ? 0 : padding);
      const center = rect.left - navRect.left - this.nav.clientLeft + rect.width / 2;
      // At either edge, reduce both sides equally instead of pushing the pill off-centre.
      const width = Math.min(desiredWidth, 2 * Math.min(center, this.nav.clientWidth - center));
      const height = Math.min(activeLabel?.getBoundingClientRect().height || rect.height + 16, this.nav.clientHeight);
      const top = (this.nav.clientHeight - height) / 2;

      this.indicator.style.height = `${height}px`;
      this.destination = { center, width, top, height };
      if (!this.motion || this.reducedMotion.matches) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.motion = { center: this.destination.center, width, centerVelocity: 0, widthVelocity: 0 };
        this.paint();
      } else if (this.frame === null) {
        this.lastTime = performance.now();
        this.frame = requestAnimationFrame(time => this.animate(time));
      }
      this.indicator.style.opacity = '1';
      this.nav.classList.toggle('kekka-nav-slider--away', Boolean(this.active && target !== this.active));
      this.nav.classList.add('kekka-nav-slider--ready');
    }

    animate(time) {
      const elapsed = Math.min((time - this.lastTime) / 1000, .064);
      this.lastTime = time;
      // A critically damped spring keeps its velocity when the next menu is hovered.
      // Unlike restarting a CSS easing curve, quick direction changes stay continuous.
      const speed = 14;
      const decay = Math.exp(-speed * elapsed);
      let settled = true;
      for (const key of ['center', 'width']) {
        const velocityKey = `${key}Velocity`;
        const distance = this.motion[key] - this.destination[key];
        const acceleration = this.motion[velocityKey] + speed * distance;
        this.motion[key] = this.destination[key] + (distance + acceleration * elapsed) * decay;
        this.motion[velocityKey] = (this.motion[velocityKey] - speed * acceleration * elapsed) * decay;
        if (Math.abs(this.motion[key] - this.destination[key]) > .1 || Math.abs(this.motion[velocityKey]) > .5) {
          settled = false;
        }
      }
      if (settled) {
        this.motion.center = this.destination.center;
        this.motion.width = this.destination.width;
        this.motion.centerVelocity = this.motion.widthVelocity = 0;
      }
      this.paint();
      this.frame = settled ? null : requestAnimationFrame(nextTime => this.animate(nextTime));
    }

    paint() {
      const scale = this.motion.width / 100;
      const left = this.motion.center - this.motion.width / 2;
      this.indicator.style.transform = `translate3d(${left}px, ${this.destination.top}px, 0) scaleX(${scale})`;
      // Compensate the horizontal radius so scaling never stretches the rounded ends.
      this.indicator.style.borderRadius = `${this.destination.height / (2 * scale)}px / ${this.destination.height / 2}px`;
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.indicator?.remove();
      this.nav?.classList.remove('kekka-nav-slider--ready', 'kekka-nav-slider--away');
      this.items?.forEach(item => item.classList.remove('kekka-nav-slider__target'));
    }
  });
}
